import {DrawIconOptions} from "../abstract-interaction";
import {AbstractTransform, TransformInteractionState} from "../abstract-transform";
import {SnapPoint} from "../../snap";
import {Feature, Point, Position} from "geojson";
import {GeometryUtil} from "../../geometry";
import * as turf from "@turf/turf";
import {DrawLineString, DrawPolygon} from "@mapbox/mapbox-gl-draw";
import {LayerGeometry} from "../../../document/model/layer";

export class EditorVertexDraw extends AbstractTransform<TransformInteractionState> {
  protected aim: Feature<Point>;

  public getVertexIcon(): string {
    return "map-pin";
  }

  buildInitialState(options: any): any {
    let state = super.buildInitialState(options);
    this.aim = this.createAIM(state.featureID, GeometryUtil.center(this.originalFeature.toGeoJSON() as Feature));
    this.displayDrawFeature(this.aim);
    return state;
  }

  public transform(state: any, e: mapboxgl.MapMouseEvent, snapPoint: SnapPoint) {
  }


  async onMouseMove(state: any, e: mapboxgl.MapMouseEvent) {
    let layerGeometry = this.controller.interaction.getLayerGeometry(state.featureID);
    this.aim = this.createAIM(
      state.featureID,
      this.controller.snapUtility.getFeaturePoint(e, {
        ...layerGeometry,
        geometry: this.transformedFeature.toGeoJSON() as Feature
      }).point as Position
    );
    this.displayDrawFeature(this.aim);
  }

 /* async onTap(state: any, e: MapTouchEvent) {
    let layerGeometry = this.controller.interaction.getLayerGeometry(state.featureID);
    this.aim = this.createAIM(
      state.featureID,
      this.controller.snapUtility.getFeaturePoint(e, {
        ...layerGeometry,
        geometry: this.transformedFeature.toGeoJSON() as Feature
      }).point as Position
    );
    this.displayDrawFeature(this.aim);
    this.onClick(state, e);
  }*/

  async onClick(state: TransformInteractionState, e: mapboxgl.MapMouseEvent | mapboxgl.MapTouchEvent): Promise<void> {
    if (!this._isMarker(e, this.getAIMIcon()) && e?.type !== "touchend") {
      return;
    }
    this.controller.interaction.historyPush(state);

    let layerGeometry: LayerGeometry = {
      ...this.controller.interaction.getLayerGeometry(state.featureID),
      geometry: this.transformedFeature.toGeoJSON() as Feature
    };

    e.preventDefault();
    let snapPoint = this.controller.snapUtility.getFeaturePoint(e, layerGeometry);
    this.aim = this.createAIM(layerGeometry.geometryId, snapPoint.point as Position);
    this.displayDrawFeature(this.aim);

    switch (layerGeometry.geometry.geometry.type) {
      case "MultiPolygon":
        this._addVertexToMultiPolygon(snapPoint, this.transformedFeature as DrawPolygon);
        break;
      case "Polygon":
        this._addVertexToPolygon(snapPoint, this.transformedFeature);
        break;
      case "LineString":
        this._addVertexToLineString(snapPoint, this.transformedFeature);
        break;
      case "MultiLineString":
        this._addVertexToMultiLineString(snapPoint, this.transformedFeature);
        break;
      default:
        throw new Error(`Geometry type ${layerGeometry.geometry.geometry.type} not handled`);
    }

    this.displayDrawFeature(this.transformedFeature);
    this.vertexMarkers.forEach(marker => this.removeDrawFeature(marker.id.toString));
    this.vertexMarkers = this._generateVertexMarkers(this.transformedFeature.toGeoJSON() as Feature, this.getVertexIcon());

    state.features.get(state.featureID).geometry = (this.transformedFeature.toGeoJSON() as Feature).geometry;

    this.tick();
  }

  protected _generateVertexMarkers(feature: Feature, icon: string, opts: Partial<DrawIconOptions> = {}): Feature[] {
    return super._generateVertexMarkers(feature, icon, {...opts, "icon-anchor": "bottom", "icon-size": 0.7});
  }


  protected _addVertexToMultiPolygon(vertex: SnapPoint, feature: DrawPolygon | DrawLineString) {
    feature.getCoordinates().forEach((subPolygon, subPolygonIndex) => {
      subPolygon.forEach((ring, ringIndex) => {
        let lastPoint = undefined;
        ring.forEach((point, pointIndex) => {
          if (lastPoint) {

            // @ts-ignore
            if (turf.pointToLineDistance(vertex.point, [point, lastPoint]) == 0) {
              feature.addCoordinate(`${subPolygonIndex}.${ringIndex}.${pointIndex}`, vertex.point[0], vertex.point[1]);
            }
          }
          lastPoint = point;
        });
      });
    });
  }

  protected _addVertexToPolygon(vertex, feature) {
    feature.getCoordinates().forEach((ring, ringIndex) => {
      let lastPoint = undefined;
      ring.forEach((point, pointIndex) => {
        if (lastPoint) {
          // @ts-ignore
          if (turf.pointToLineDistance(vertex.point, [point, lastPoint]) == 0) {
            feature.addCoordinate(`${ringIndex}.${pointIndex}`, vertex.point[0], vertex.point[1]);
          }
        }
        lastPoint = point;
      });
    });
  }

  _addVertexToLineString(vertex, feature) {
    let lastPoint = undefined;
    feature.getCoordinates().forEach((point, pointIndex) => {
      if (lastPoint) {
        // @ts-ignore
        if (turf.pointToLineDistance(vertex.point, [point, lastPoint]) == 0) {
          feature.addCoordinate(`${pointIndex}`, vertex.point[0], vertex.point[1]);
        }
      }
      lastPoint = point;
    })
  }

  _addVertexToMultiLineString(vertex, feature) {
    let lastPoint = undefined;
    feature.getCoordinates().forEach((subLine, subLineIndex) => {
      subLine.forEach((point, pointIndex) => {
        if (lastPoint) {
          // @ts-ignore
          if (turf.pointToLineDistance(vertex.point, [point, lastPoint]) == 0) {
            feature.addCoordinate(`${subLineIndex}.${pointIndex}`, vertex.point[0], vertex.point[1]);
          }
        }
        lastPoint = point;
      });
    })
  }
}
